import { useQuery } from '@tanstack/react-query'
import { Edit, FileText, Loader2, Save, X } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { InvestigationDigestCard } from '@/components/case-report/investigation-digest-card'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { HypothesesDigest } from '@/features/analytic-hypotheses/HypothesesDigest'
import { CaseNotesList } from '@/features/case-notes/CaseNotesList'
import { CaseSummaryCard } from '@/features/case-overview/CaseSummaryCard'
import { CaseEntitiesCard } from '@/features/case-report/CaseEntitiesCard'
import { useCaseDescriptionEditor } from '@/features/cases/hooks/useCaseDescriptionEditor'
import { caseKeys } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import { investigationsApi } from '@/services/api/investigations'
import { useCaseContext } from '../../CaseContext'
import { ReportExportButton, type ReportOutletContext } from '../../ReportLayout'

export default function ReportSummaryPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const { handleExportPdf, isGeneratingReport } = useOutletContext<ReportOutletContext>()
  const { data: caseData } = useQuery({
    queryKey: caseKeys.detail(caseId),
    queryFn: () => api.cases.get(caseId),
  })
  const { data: investigations = [] } = useQuery({
    queryKey: ['case-investigations', caseId],
    queryFn: () => investigationsApi.list(caseId),
  })
  const descriptionEditor = useCaseDescriptionEditor(caseId, caseData?.description)

  const promotedInvestigations = useMemo(
    () =>
      investigations
        .filter(inv => inv.includedInReport)
        .sort((a, b) => {
          const aTime = a.reportAddedAt ? new Date(a.reportAddedAt).getTime() : 0
          const bTime = b.reportAddedAt ? new Date(b.reportAddedAt).getTime() : 0
          return bTime - aTime
        }),
    [investigations]
  )

  return (
    <PageShell
      layout="padded"
      icon={FileText}
      title={t('cases.report.title')}
      summary={t('cases.report.summary')}
      toolbar={<ReportExportButton onExport={handleExportPdf} isExporting={isGeneratingReport} />}
    >
      <Card className="p-4 space-y-4 md:p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">{t('cases.report.caseSummary')}</h3>
          {!descriptionEditor.isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={descriptionEditor.startEditing}
              className="h-9 text-xs md:h-7"
            >
              <Edit className="size-3 mr-1" />
              {t('common.edit')}
            </Button>
          )}
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          {t(
            'cases.report.caseSummaryIntro',
            'A short narrative briefing for this case — who is involved, what is being investigated, and what was found. This is the executive header that opens the generated report and frames every investigation below.'
          )}
        </p>

        {descriptionEditor.isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={descriptionEditor.description}
              onChange={e => descriptionEditor.setDraft(e.target.value)}
              placeholder={t('cases.report.summaryPlaceholder')}
              rows={6}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={descriptionEditor.save}
                disabled={descriptionEditor.isSaving}
                className="h-9 md:h-8"
              >
                {descriptionEditor.isSaving ? (
                  <Loader2 className="size-3 mr-1 animate-spin" />
                ) : (
                  <Save className="size-3 mr-1" />
                )}
                {descriptionEditor.isSaving ? t('cases.report.saving') : t('common.save')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={descriptionEditor.cancelEditing}
                disabled={descriptionEditor.isSaving}
                className="h-9 md:h-8"
              >
                <X className="size-3 mr-1" />
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {descriptionEditor.description || t('cases.report.noSummary')}
          </p>
        )}

        {/* The case's key judgments open the report with the narrative (PRD-0075 R-E5). */}
        <HypothesesDigest caseId={caseId} />
      </Card>

      {/* AI Case Summary — the state-driven card that walks the user from
          "no eligible investigations" → "generate" → "view + regenerate +
          download PDF". The per-investigation mini-list is suppressed here
          because the page already renders ``InvestigationDigestCard`` for
          every promoted investigation below. */}
      <CaseSummaryCard caseId={caseId} showInvestigationList={false} />

      <CaseEntitiesCard caseId={caseId} />

      {promotedInvestigations.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <FileText className="size-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">
              {t('cases.report.investigationViewsTitle')}
            </h3>
            <span className="font-mono text-2xs text-muted-foreground">
              {promotedInvestigations.length}
            </span>
          </div>
          <div className="space-y-4">
            {promotedInvestigations.map((investigation, idx) => (
              <InvestigationDigestCard
                key={investigation.id}
                investigation={investigation}
                caseId={caseId}
                index={idx + 1}
              />
            ))}
          </div>
        </section>
      )}

      <Card className="p-4 md:p-6">
        <CaseNotesList caseId={caseId} currentUserName="Current User" />
      </Card>
    </PageShell>
  )
}
